import React, { useEffect, useState } from 'react'
import { Button, Stepper, Step, StepLabel, SelectChangeEvent } from '@mui/material'
import { Box } from '@mui/system'
import EventDetails from './Steps/EventDetails'
import useEventFormHook from '../../../../hooks/eventHooks/useEventFormHook'
import EventSchedule from './Steps/EventSchedule'
import { Event } from '../../../../redux/event/manageEvents/types'
import EventUpload from './Steps/EventUpload'
import { useLoading } from '../../../../context/LoadingContext'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import LoadingBackdrop from '../../../Common/Loading/LoadingBackdrop'
// import EventGuests from './Steps/EventGuests'
import useEventForm, { FormData } from '../../../../hooks/useEventForm'

interface EventFormProps {
  eventDetails?: Event
  onSave: () => void
  onChange: (hasChanges: boolean) => void
}

const STEPS = ['Details', 'Schedule & Location', 'Upload Image'] as const

type ErrorType = {
  [K in keyof FormData]?: string
}

const EventForm: React.FC<EventFormProps> = ({ eventDetails, onSave, onChange }) => {
  const { loading } = useLoading()
  const { handleSubmit: submitForm, handleComplete: submitComplete } = useEventFormHook(eventDetails?.id)
  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()
  const [activeStep, setActiveStep] = useState(0)

  const {
    formData,
    errors,
    setErrors,
    imageFile,
    setImageFile,
    handleChange,
    validateStep,
    hasFormChanges,
  } = useEventForm(eventDetails)

  useEffect(() => {
    const formChanged = hasFormChanges()
    onChange(formChanged)
  }, [hasFormChanges, onChange, formData, eventDetails])

  const typedErrors = errors as ErrorType

  const isUpdateEvent = Boolean(eventDetails?.id)

  const handleNext = () => {
    const stepErrors = validateStep(activeStep)
    if (Object.values(stepErrors).some((error) => error)) {
      setErrors((prevErrors: Record<string, string>) => ({ ...prevErrors, ...stepErrors }))
      return
    }
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleInputChange = (
    eventOrValue:
      | string
      | Date
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
      | null,
    fieldName?: string,
  ) => {
    if (fieldName) {
      handleChange(fieldName as keyof FormData, eventOrValue)
    } else if (eventOrValue && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
      handleChange(eventOrValue.target.name as keyof FormData, eventOrValue.target.value)
    }
  }

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate all steps before submitting
    const allStepErrors = {
      ...validateStep(0),
      ...validateStep(1),
    }

    if (Object.values(allStepErrors).some((error) => error)) {
      setErrors((prevErrors: Record<string, string>) => ({ ...prevErrors, ...allStepErrors }))
      return
    }

    openConfirmationAlert({
      title: isUpdateEvent ? 'Update Event' : 'Create New Event',
      text: isUpdateEvent
        ? 'Are you sure you want to update this event?'
        : 'Are you sure you want to create new event?',
      onConfirm: async () => {
        await submitForm({ ...formData, imageFile })
        onSave()
      },
    })
  }

  const handleComplete = () => {
    openConfirmationAlert({
      title: 'Complete event',
      text: 'Are you sure you want to complete this event?',
      onConfirm: async () => {
        await submitComplete()
        onSave()
      },
    })
  }

  const handleClear = () => {
    setActiveStep(0)
    Object.keys(formData).forEach((key) => {
      handleChange(key as keyof FormData, '')
    })
    setImageFile(undefined)
  }

  const isValueEmpty = (value: any) => {
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (value && typeof value === 'object') return Object.keys(value).length === 0
    return value === null || value === undefined
  }

  const isFormEmpty = React.useMemo(
    () => Object.values(formData).every(isValueEmpty) && !imageFile,
    [formData, imageFile],
  )

  useEffect(() => {
    if (onChange) {
      onChange(hasFormChanges() || !isFormEmpty)
    }
  }, [formData, imageFile, hasFormChanges, onChange])

  const detailsError = {
    title: typedErrors.title || '',
    details: typedErrors.details || '',
    estimatedBudget: typedErrors.estimatedBudget || '',
    targetCompliance: typedErrors.targetCompliance || '',
    category: typedErrors.category || '',
    type: typedErrors.type || '',
  }

  const scheduleError = {
    startDate: typedErrors.startDate || '',
    endDate: typedErrors.endDate || '',
    venue: typedErrors.venue || '',
    importance: typedErrors.importance || '',
    googleMeetLink: typedErrors.googleMeetLink || '',
    postSurveyURL: typedErrors.postSurveyURL || '',
  }

  const StepContent = [
    <EventDetails
      key='details'
      formData={formData}
      handleChange={handleInputChange}
      error={detailsError}
    />,
    <EventSchedule
      key='schedule'
      formData={formData}
      handleChange={handleInputChange}
      error={scheduleError}
    />,
    // <EventGuests key='guests' handleChange={handleInputChange} />,
    <EventUpload
      key='upload'
      eventDetails={eventDetails}
      handleChange={handleInputChange}
      setImageFile={setImageFile}
      imageFile={imageFile}
    />,
  ]

  return (
    <form onSubmit={handleFormSubmit} autoComplete='off'>
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ my: 2 }}>{StepContent[activeStep]}</Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>{activeStep !== 0 && <Button onClick={handleBack}>Back</Button>}</Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {isUpdateEvent && eventDetails?.status === 'active' && (
              <Button variant='contained' color='success' onClick={handleComplete}>
                Complete
              </Button>
            )}{' '}
            {!isUpdateEvent && (
              <Button color='info' onClick={handleClear} disabled={isFormEmpty}>
                Clear
              </Button>
            )}
            {activeStep === STEPS.length - 1 && (
              <Button type='submit' variant='contained' color={isUpdateEvent ? 'primary' : 'success'}>
                {isUpdateEvent ? 'Update' : 'Create'}
              </Button>
            )}
            {activeStep !== STEPS.length - 1 && (
              <Button variant='contained' color='primary' onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {loading && (
        <LoadingBackdrop message={isUpdateEvent ? 'Updating event...' : 'Creating event...'} />
      )}
      {ConfirmationAlert}
    </form>
  )
}

export default EventForm
